"use client";

import { useState } from "react";
import { Info } from "lucide-react";
import StepHeading from "@/app/components/molecules/StepHeading";
import FormCard from "@/app/components/atoms/FormCard";
import Button from "@/app/components/atoms/Button";

const ALTRU_REGISTRATION_URL = `${process.env.NEXT_PUBLIC_ALTRU_BASE_URL ?? ""}/${process.env.NEXT_PUBLIC_ALTRU_REGISTRATION_PATH ? `${process.env.NEXT_PUBLIC_ALTRU_REGISTRATION_PATH}&backto=memberportal` : "/page.aspx?pid=204&backto=memberportal"}`;

interface Props {
  onContinue: () => void;
  loading?: boolean;
}

/**
 * @component RegisterNoAltruAccountStep
 * @description Shown when the registering email has no Altru constituent record.
 * Instructs the user to create a GFS Ticketing account first, then return to continue.
 * Step 1 opens the Altru registration page. Step 2 calls onContinue to re-run the
 * email check with fromAltru=true — avoids same-URL no-op navigation.
 * @returns A two-step instructional card with external link and confirmation button.
 */
export default function RegisterNoAltruAccountStep({ onContinue, loading }: Props) {
  const [hasClickedRegistration, setHasClickedRegistration] = useState(false);

  return (
    <div className="flex flex-col gap-8">
      <StepHeading id="register-no-altru-header" title="Create an account" />

      <div className="w-full max-w-md m-auto">
        <FormCard>
          <div className="flex justify-center">
            <Info className="w-14 h-14 text-charcoal" aria-hidden />
          </div>

          <p className="text-body-base text-charcoal pb-6 border-b border-card-stroke">
            Before you continue, create an account in GFS Ticketing.
          </p>

          <div className="flex flex-col gap-6">
            <div className="flex gap-4 items-start">
              <span className="shrink-0 w-10 h-10 flex items-center justify-center text-xl font-black text-charcoal border-2 border-charcoal font-arquitecta">
                1
              </span>
              <p className="text-body-base text-charcoal pt-1">
                Click the button to create an account in our ticketing platform.
              </p>
            </div>

            <Button
              href={ALTRU_REGISTRATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant={"outline"}
              size="full"
              onClick={() => setHasClickedRegistration(true)}
            >
              Continue to ticketing registration
            </Button>

            <div className="border-b border-card-stroke" aria-hidden />

            <div className="flex gap-4 items-start">
              <span className="shrink-0 w-10 h-10 flex items-center justify-center text-xl font-black text-charcoal border-2 border-charcoal font-arquitecta">
                2
              </span>
              <p className="text-body-base text-charcoal pt-1">
                Return and click the button below.
              </p>
            </div>

            <Button
              variant="accent"
              size="full"
              disabled={!hasClickedRegistration}
              loading={loading}
              onClick={onContinue}
            >
              I made a ticketing account, continue
            </Button>
          </div>
        </FormCard>
      </div>
    </div>
  );
}
